import { useState, useEffect, useMemo, useCallback, useContext } from 'react'
import { useMutation, useLazyQuery } from "@apollo/client";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router";
import {
  DYNOESCOUNT,
  DYNOES,
} from "../../graphql/queries";
import { DELETE_DYNO, COPY_DYNO, UPDATE_DYNO } from "../../graphql/mutations";
import { useBreadcrumbs } from "../../contexts/BreadcrumbProvider"
import { editDynoUrl } from "../../utils/helper";
import { READYFORQA } from "../../constants";
import { PublisherAndUserContext } from '../../contexts/PublisherAndUserContext';

let PageSize = 10;

export const useDynoes = () => {
  const {user, publisher} = useContext(PublisherAndUserContext);
  const [dynoes, setDynoes] = useState([]);
  const [searchedDynoes, setSearchedDynoes] = useState([]);
  const [totalDynoes, setTotalDynoes] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDynoId, setSelectedDynoId] = useState("");
  const [catalogs, setCatalogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [dynoList, setDynoList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState("");
  const { onClose, isOpen, onOpen } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const { addMultiBreadCrumb } = useBreadcrumbs();

  const [getDynoesCount] = useLazyQuery(DYNOESCOUNT, {
    onCompleted: (data) => {
      if (data.findDynoCount) {
        setTotalDynoes(data.findDynoCount);
        getDynoes({
          variables: {
            take: 10,
            skip: 0,
            orderBy: {
              createdAt: "desc",
            },
            where: {
              owner: {
                id: {
                  equals: user.id,
                },
              },
            },
          },
        });
      }
    },
    fetchPolicy: "no-cache",
  });

  const [searchMyDynoes, { loading: searchMyDynoesLoading }] = useLazyQuery(
    DYNOES,
    {
      onCompleted: (data) => {
        const dynoesRes = data.dynoes;
        setSearchedDynoes(dynoesRes);
      },
      notifyOnNetworkStatusChange: true,
      onError: () => {
        console.log("error while fetching or searching EdDoc.");
      },
      fetchPolicy: "no-cache",
    }
  );

  const [
    getDynoes,
    { loading: myDynoesLoading, fetchMore: myDynoesFetchMore },
  ] = useLazyQuery(DYNOES, {
    onCompleted: (data) => {
      const dynoesRes = data.dynoes;
      if (currentPage > 1) {
        const allDynoes = [...dynoes];
        const index = currentPage * 10 - 10;
        allDynoes.splice(index, 10, ...dynoesRes);
        setDynoes(allDynoes);
      } else {
        const dynoesInit = new Array(totalDynoes);
        dynoesInit.splice(0, 10, ...dynoesRes);
        setDynoes(dynoesInit);
      }
    },
    notifyOnNetworkStatusChange: true,
    onError: () => {
      console.log("error while fetching or searching dyno.");
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const [deleteDyno] = useMutation(DELETE_DYNO, {
    onCompleted: (data) => {
      setDynoes(dynoes.filter((dyno) => dyno.id !== data.edshopifyDeleteDyno.id));
      setLoading(false);
    },
    onError: () => {
      toast({
        position: "top-right",
        description: `Failed to delete EdDoc, as it is part of a catalog/bundle.`,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
    },
  });

  const [copyDyno] = useMutation(COPY_DYNO, {
    onCompleted: (data) => {
      window.open(editDynoUrl(data.copyDyno.id), "_self");
      setDynoes([...dynoes, data.copyDyno]);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const [updateDyno] = useMutation(UPDATE_DYNO, {
    onCompleted: (data) => {
      const dyno = data.updateDyno;
      updateDynoCallback(dyno);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    if (user && user?.id) {
      getDynoesCount({
        variables: {
          where: {
            owner: {
              id: {
                equals: user.id,
              },
            },
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText !== "") {
        searchMyDynoes({
          variables: {
            orderBy: {
              createdAt: "desc",
            },
            take: 10,
            where: {
              owner: {
                id: {
                  equals: user.id,
                },
              },
              dynoTitleNoCase: {
                contains: searchText.toLowerCase(),
              },
            },
          },
        });
      }
    }, 400);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const onPageChange = (page) => {
    if (!dynoes[(page - 1) * 10]) {
      myDynoesFetchMore({
        variables: {
          take: 10,
          skip: Math.round(page * 10) - 10,
        },
      });
    }
    setCurrentPage(page);
  };

  const deleteDynoCallback = useCallback((dyno) => {
    if (
      dyno.dynoPublishingStatus === "NotStarted" ||
      dyno.dynoPublishingStatus === "InProgress" ||
      !dyno.dynoPublishingStatus
    ) {
      setLoading(true);
      deleteDyno({
        variables: {
          where: {
            id: publisher?.id,
            dynoId: dyno.id
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyDynoCallback = useCallback((dyno) => {
    setLoading(true);
    copyDyno({
      variables: {
        where: {
          id: dyno.id,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareCallback = useCallback((dyno) => {
    setSelectedDynoId(dyno.id);
    onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareStatusCallback = useCallback((dyno) => {
    setLoading(true);
    updateDyno({
      variables: {
        where: {
          id: dyno.id,
        },
        data: {
          dynoPublishingStatus: { set: READYFORQA },
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDynoCallback = (dyno) => {
    const updatedDynoes = [...dynoes];
    const index = updatedDynoes.findIndex(
      (dynoe) => !!dynoe && dynoe.id === dyno.id
    );
    if (index > -1) {
      updatedDynoes[index] = {
        ...updatedDynoes[index],
        dynoPublishingStatus: dyno.dynoPublishingStatus,
      };
      setDynoes(updatedDynoes);
    }
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const setListCallback = useCallback((type, list) => {
    if (type === "catalogs") {
      setCatalogs(list);
    } else if (type === "products") {
      setProducts(list);
    } else if (type === "dynoList") {
      setDynoList(list);
    }
  }, []);

  const searchTextCallback = useCallback((text) => {
    setSearchText(text);
  }, []);

  const currentDynosList = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    const resDynoes = dynoes.slice(firstPageIndex, lastPageIndex);
    if (!resDynoes[0]) {
      return [];
    } else {
      return resDynoes;
    }
  }, [currentPage, dynoes]);

  const openContent = (product) => {
    addMultiBreadCrumb([{ label: "Home", path: "/" }, { label: "Catalog", path: "/catalogs" }, { label: product?.catalog?.catalogName, path: `/catalog/${product?.catalog?.id}` }, { label: product.productName, path: "" }])
    navigate(`/content/${product.id}`)
  }

  return {
    publisher,
    searchText,
    searchTextCallback,
    myDynoesLoading,
    searchMyDynoesLoading,
    totalDynoes,
    searchedDynoes,
    currentDynosList,
    copyDynoCallback,
    deleteDynoCallback,
    shareCallback,
    shareStatusCallback,
    currentPage,
    onPageChange,
    selectedDynoId,
    updateDynoCallback,
    catalogs,
    products,
    dynoList,
    setListCallback,
    onClose,
    isOpen,
    PageSize,
    loading,
    openContent
  }
}
